import { useState, useRef, useEffect, useMemo, ReactNode } from "react";
import { createPortal } from "react-dom";
import { UserCog, Loader2, AlertCircle, Search, ChevronDown, Check } from "lucide-react";

interface Candidate {
  id: number;
  name: string;
}

interface Props {
  candidates: Candidate[];
  currentEmployeeId: number | null | undefined;
  onReassign: (employeeId: number) => Promise<void>;
  children: ReactNode;
}

const AVATAR_PALETTE = [
  "bg-indigo-500/15 text-indigo-300 border-indigo-500/30",
  "bg-violet-500/15 text-violet-300 border-violet-500/30",
  "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "bg-rose-500/15 text-rose-300 border-rose-500/30",
  "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
];

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

function paletteFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

/** Click-to-toggle popover for reassigning a job/alert to a different Technical Manager.
 * Wraps a trigger (the assignee chip) and, on click, shows a searchable dropdown of every
 * candidate except whoever is currently assigned. Right-anchored to the trigger so it never
 * overflows off-screen when the chip sits near the card's right edge; a small caret ties it
 * visually back to the trigger. */
export default function ReassignPopover({ candidates, currentEmployeeId, onReassign, children }: Props) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const allOptions = useMemo(
    () => candidates.filter((c) => c.id !== currentEmployeeId),
    [candidates, currentEmployeeId],
  );
  const options = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allOptions;
    return allOptions.filter((c) => c.name.toLowerCase().includes(term));
  }, [allOptions, search]);

  // Entrance transition: mount at scale/opacity 0, flip to visible on next frame.
  useEffect(() => {
    if (open) {
      setEntered(false);
      const raf = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [open]);

  // Focus something inside the popover so keyboard nav (arrows/enter/escape) works even
  // when there's no search box to grab focus (short candidate lists) — without this the
  // trigger button (outside the portal subtree) would keep focus and keydown would never
  // reach our handler below.
  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      if (searchRef.current) searchRef.current.focus();
      else containerRef.current?.focus();
    });
  }, [open]);

  useEffect(() => setFocusedIndex(0), [search]);

  function openPopover(e: React.MouseEvent) {
    e.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    }
    setError(null);
    setSearch("");
    setFocusedIndex(0);
    setOpen(true);
  }

  function close() {
    setOpen(false);
    setError(null);
  }

  async function handleSelect(employeeId: number) {
    setSavingId(employeeId);
    setError(null);
    try {
      await onReassign(employeeId);
      close();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to reassign. Please try again.");
    } finally {
      setSavingId(null);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocusedIndex((i) => Math.min(i + 1, options.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setFocusedIndex((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); const c = options[focusedIndex]; if (c) handleSelect(c.id); }
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPopover}
        className={`group inline-flex items-center gap-1 rounded transition-all ${open ? "ring-2 ring-primary/50" : "hover:ring-1 hover:ring-primary/40"}`}
      >
        {children}
        <ChevronDown className="w-2.5 h-2.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
      </button>

      {open && pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={(e) => { e.stopPropagation(); close(); }} />
            <div
              ref={containerRef}
              tabIndex={-1}
              className={`fixed z-[81] w-72 origin-top-right transition-all duration-150 ease-out outline-none ${
                entered ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 -translate-y-1"
              }`}
              style={{ top: pos.top, right: pos.right }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
            >
              {/* Caret pointing back up to the trigger */}
              <div className="absolute -top-1.5 right-5 w-3 h-3 bg-surface-muted border-t border-l border-border/80 rotate-45" />

              <div className="relative bg-surface-muted border border-border/80 rounded-2xl shadow-2xl shadow-black/50 overflow-hidden">
                <div className="flex items-center gap-2.5 px-4 py-3 bg-gradient-to-b from-surface/40 to-surface-muted border-b border-border/80">
                  <div className="w-7 h-7 rounded-lg bg-primary/15 border border-primary/25 flex items-center justify-center shrink-0">
                    <UserCog className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold text-foreground font-inter leading-tight">Reassign job</p>
                    <p className="text-[10.5px] text-muted-foreground font-inter leading-tight mt-0.5">Choose a Technical Manager</p>
                  </div>
                </div>

                {allOptions.length > 5 && (
                  <div className="px-3 pt-2.5 pb-1.5 border-b border-border/60">
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        ref={searchRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search managers…"
                        className="w-full bg-surface/60 border border-border rounded-lg pl-8 pr-2.5 py-1.5 text-[11.5px] text-foreground placeholder-muted-foreground font-inter focus:outline-none focus:border-primary/60 transition-colors"
                      />
                    </div>
                  </div>
                )}

                <div className="max-h-60 overflow-y-auto py-1.5">
                  {options.length === 0 ? (
                    <div className="flex flex-col items-center gap-1.5 px-4 py-7 text-center">
                      <UserCog className="w-5 h-5 text-muted-foreground" />
                      <p className="text-[11px] text-muted-foreground font-inter">
                        {allOptions.length === 0 ? "No other active Technical Managers." : "No matches found."}
                      </p>
                    </div>
                  ) : (
                    options.map((c, i) => {
                      const isFocused = i === focusedIndex;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          disabled={savingId !== null}
                          onMouseEnter={() => setFocusedIndex(i)}
                          onClick={() => handleSelect(c.id)}
                          className={`relative w-full flex items-center gap-2.5 pl-3.5 pr-3 py-2 mx-1 rounded-lg text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                            isFocused ? "bg-primary/10" : ""
                          }`}
                          style={{ width: "calc(100% - 0.5rem)" }}
                        >
                          {isFocused && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-full bg-primary" />}
                          <div className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 text-[10px] font-bold font-inter ${paletteFor(c.name)}`}>
                            {initialsOf(c.name)}
                          </div>
                          <span className={`flex-1 min-w-0 truncate text-[12px] font-inter transition-colors ${isFocused ? "text-foreground" : "text-muted-foreground"}`}>
                            {c.name}
                          </span>
                          {savingId === c.id ? (
                            <Loader2 className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />
                          ) : isFocused ? (
                            <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>

                {error && (
                  <div className="flex items-start gap-1.5 px-4 py-2.5 bg-red-500/10 border-t border-red-500/20">
                    <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-px" />
                    <p className="text-[10.5px] text-red-400 font-inter leading-snug">{error}</p>
                  </div>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}
